import React, { useState, useEffect, useCallback } from 'react';
import {
  RiAddLine, RiDeleteBinLine, RiEditLine, RiArrowLeftLine, RiSaveLine,
  RiArrowUpSLine, RiArrowDownSLine, RiFileCopyLine,
} from 'react-icons/ri';
import {
  adminGetGrammarChapters, adminGetGrammarChapter, adminCreateGrammarChapter,
  adminUpdateGrammarChapter, adminDeleteGrammarChapter,
} from '../../api/grammar.api';
import { C } from '../../data/constants';
import {
  PageHeader, LoadingScreen, EmptyState, ConfirmModal, Toast,
} from '../../components/shared/UI';

/* ══════════════════════════════════════════════════════════════
   GRAMMAR CHAPTER MANAGER
   Chapter › part › sentence › token, edited as one document and
   saved in one request — the same whole-document approach the
   practice-test editor uses for sections and questions.

   The token table is the important part of this screen. A sentence
   with no tokens still displays, but it cannot generate any drills,
   so the editor says so rather than letting it fail silently.
══════════════════════════════════════════════════════════════ */

const ROLES = [
  'noun', 'pronoun', 'name', 'verb', 'i-adjective', 'na-adjective',
  'particle', 'copula', 'adverb', 'question', 'number', 'counter',
  'conjunction', 'prefix', 'suffix', 'expression', 'other',
];

const emptyToken = () => ({ jp: '', romaji: '', gloss: '', role: 'noun', note: '', silentInEnglish: false });
const emptyEnToken = () => ({ text: '', jp: '', romaji: '' });
const emptySentence = () => ({
  jp: '', romaji: '', en: '', literal: '', note: '',
  tokens: [emptyToken()],
  enTokens: [],
  drills: ['gloss', 'translate', 'build', 'particle'],
});
const emptyPart = index => ({
  index, title: '', pattern: '', patternEn: '', explanation: '',
  notes: [], sentences: [], isPublished: true,
});
const emptyChapter = number => ({
  number, level: 'N5', title: '', titleEn: '', summary: '',
  intro: '', keyPoints: [], parts: [], isPublished: false,
});

const move = (arr, from, to) => {
  if (to < 0 || to >= arr.length) return arr;
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export default function GrammarChapterManager() {
  const [chapters, setChapters] = useState([]);
  const [draft, setDraft] = useState(null);     // the chapter being edited
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [openPart, setOpenPart] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    adminGetGrammarChapters()
      .then(res => setChapters(res.data || []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = msg => { setNotice(msg); setTimeout(() => setNotice(''), 2600); };

  const edit = async id => {
    setError('');
    try {
      const res = await adminGetGrammarChapter(id);
      setDraft(res.data);
      setOpenPart(0);
    } catch (err) { setError(err.message); }
  };

  const startNew = () => {
    const next = Math.max(0, ...chapters.map(c => c.number)) + 1;
    setDraft(emptyChapter(next));
    setOpenPart(0);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (draft._id) await adminUpdateGrammarChapter(draft._id, draft);
      else await adminCreateGrammarChapter(draft);
      flash(`Chapter ${draft.number} saved`);
      setDraft(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const [pendingDelete, setPendingDelete] = useState(null);

  const remove = async () => {
    const { _id, number } = pendingDelete;
    setPendingDelete(null);
    try {
      await adminDeleteGrammarChapter(_id);
      flash(`Chapter ${number} deleted`);
      load();
    } catch (err) { setError(err.message); }
  };

  const set = (field, value) => setDraft(d => ({ ...d, [field]: value }));

  const setPart = (pi, field, value) => setDraft(d => ({
    ...d,
    parts: d.parts.map((p, i) => (i === pi ? { ...p, [field]: value } : p)),
  }));

  const setSentence = (pi, si, field, value) => setDraft(d => ({
    ...d,
    parts: d.parts.map((p, i) => (i !== pi ? p : {
      ...p,
      sentences: p.sentences.map((s, j) => (j === si ? { ...s, [field]: value } : s)),
    })),
  }));

  const setEnToken = (pi, si, ti, field, value) => setDraft(d => ({
    ...d,
    parts: d.parts.map((p, i) => (i !== pi ? p : {
      ...p,
      sentences: p.sentences.map((s, j) => (j !== si ? s : {
        ...s,
        enTokens: s.enTokens.map((t, k) => (k === ti ? { ...t, [field]: value } : t)),
      })),
    })),
  }));

  /* Splits the English sentence into tiles and guesses each one's Japanese
     counterpart by matching the word against the tokens' glosses. Always a
     starting point, never the final answer — word order differs between the
     languages, so the author fixes up what the guess got wrong. */
  const deriveEnTokens = (pi, si) => setDraft(d => ({
    ...d,
    parts: d.parts.map((p, i) => (i !== pi ? p : {
      ...p,
      sentences: p.sentences.map((s, j) => {
        if (j !== si) return s;
        const words = (s.en || '').split(/\s+/).filter(Boolean);
        return {
          ...s,
          enTokens: words.map(w => {
            const key = w.toLowerCase().replace(/[.,!?]/g, '');
            const hit = s.tokens.find(tok => {
              const gloss = (tok.gloss || '').toLowerCase();
              return gloss && (gloss === key || gloss.split(/\s*\/\s*/).includes(key));
            });
            return { text: w, jp: hit?.jp || '', romaji: hit?.romaji || '' };
          }),
        };
      }),
    })),
  }));

  const setToken = (pi, si, ti, field, value) => setDraft(d => ({
    ...d,
    parts: d.parts.map((p, i) => (i !== pi ? p : {
      ...p,
      sentences: p.sentences.map((s, j) => (j !== si ? s : {
        ...s,
        tokens: s.tokens.map((t, k) => (k === ti ? { ...t, [field]: value } : t)),
      })),
    })),
  }));

  /* Fills the whole-sentence fields from the tokens, so an author types each
     word once instead of retyping the sentence three times and keeping the
     copies in sync by hand. */
  const deriveFromTokens = (pi, si) => setDraft(d => ({
    ...d,
    parts: d.parts.map((p, i) => (i !== pi ? p : {
      ...p,
      sentences: p.sentences.map((s, j) => {
        if (j !== si) return s;
        const toks = s.tokens.filter(t => t.jp.trim());
        return {
          ...s,
          jp: toks.map(t => t.jp).join(''),
          romaji: toks.map(t => t.romaji).filter(Boolean).join(' '),
          literal: toks.map(t => t.gloss || '~').join(' — '),
        };
      }),
    })),
  }));

  if (loading) return <LoadingScreen label="Loading chapters" />;

  /* ── List view ──────────────────────────────────────────────── */
  if (!draft) {
    return (
      <div className="grm-admin anim-fade-up">
        <PageHeader
          title="Grammar Chapters"
          sub={`${chapters.length} chapters · Minna no Nihongo structure`}
          action={<button className="btn-primary" onClick={startNew}><RiAddLine /> New chapter</button>}
        />

        {error && <div className="grm-error">{error}</div>}

        {chapters.length === 0 && (
          <div className="ks-card">
            <EmptyState
              icon={RiEditLine}
              title="No chapters yet"
              sub="Create chapter 1, or run utils/seedGrammar.js to lay down the 25-chapter skeleton."
            />
          </div>
        )}

        <div className="grm-table-wrap">
          <table className="ks-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>Title</th>
                <th style={{ width: 90 }}>Parts</th>
                <th style={{ width: 110 }}>Sentences</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {chapters.map(c => (
                <tr key={c._id}>
                  <td><strong>{c.number}</strong></td>
                  <td>
                    <div style={{ fontFamily: "'Noto Sans JP', sans-serif", fontWeight: 800 }}>{c.title}</div>
                    <div style={{ fontSize: 12, opacity: 0.55 }}>{c.titleEn}</div>
                  </td>
                  <td>{c.partsTotal}</td>
                  <td>{c.sentenceCount}</td>
                  <td>
                    <span className="badge" style={{
                      color: c.isPublished ? C.green : C.muted,
                      background: c.isPublished ? `${C.green}18` : 'rgba(255,255,255,0.05)',
                    }}>
                      {c.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-icon" onClick={() => edit(c._id)} title="Edit"><RiEditLine size={15} /></button>
                      <button className="btn-icon danger" onClick={() => setPendingDelete(c)} title="Delete"><RiDeleteBinLine size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pendingDelete && (
          <ConfirmModal
            message={`Delete chapter ${pendingDelete.number}? Learner progress is kept, but the content is gone.`}
            onConfirm={remove}
            onCancel={() => setPendingDelete(null)}
          />
        )}
        {notice && <Toast message={notice} />}
      </div>
    );
  }

  /* ── Editor ─────────────────────────────────────────────────── */
  return (
    <div className="grm-admin anim-fade-up">
      <button className="gr-back" onClick={() => setDraft(null)}>
        <RiArrowLeftLine size={14} /> All chapters
      </button>

      <PageHeader
        title={draft._id ? `Edit chapter ${draft.number}` : 'New chapter'}
        sub={draft.titleEn || 'Chapter › part › sentence › word'}
        action={(
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? <div className="spinner" /> : <><RiSaveLine /> Save chapter</>}
          </button>
        )}
      />

      {error && <div className="grm-error">{error}</div>}

      <div className="ks-card grm-form">
        <div className="grm-form-section">Identity <span /></div>

        <div className="ks-grid-2">
          <label>Number
            <input className="ks-input" type="number" min="1" value={draft.number}
                   onChange={e => set('number', Number(e.target.value))} />
          </label>
          <label>Level
            <select className="ks-select" value={draft.level} onChange={e => set('level', e.target.value)}>
              {['N5', 'N4', 'N3', 'N2', 'N1'].map(l => <option key={l}>{l}</option>)}
            </select>
          </label>
        </div>

        <label>Title (Japanese)
          <input className="ks-input jp" value={draft.title} onChange={e => set('title', e.target.value)}
                 placeholder="わたしは がくせいです" />
        </label>
        <label>Title (English)
          <input className="ks-input" value={draft.titleEn} onChange={e => set('titleEn', e.target.value)}
                 placeholder="I am a student" />
        </label>
        <label>Summary — one line for the chapter card
          <input className="ks-input" value={draft.summary} onChange={e => set('summary', e.target.value)} />
        </label>

        <div className="grm-form-section">Briefing <span /></div>

        <label>Introduction — what this grammar is and why it exists. Blank line between paragraphs.
          <textarea className="ks-textarea" rows={9} value={draft.intro}
                    onChange={e => set('intro', e.target.value)} />
        </label>

        <label>Key points — one per line
          <textarea className="ks-textarea" rows={4} value={(draft.keyPoints || []).join('\n')}
                    onChange={e => set('keyPoints', e.target.value.split('\n').filter(Boolean))} />
        </label>

        <div className="grm-form-section">Visibility <span /></div>

        <label className="grm-check">
          <input type="checkbox" checked={draft.isPublished}
                 onChange={e => set('isPublished', e.target.checked)} />
          Published — visible to learners
        </label>
      </div>

      {/* ── Parts ───────────────────────────────────────────────── */}
      <div className="grm-subhead" style={{ marginTop: 26 }}>
        <h2 style={{ fontSize: 16, fontWeight: 900 }}>Parts ({draft.parts.length})</h2>
        <button className="btn-ghost" onClick={() => {
          setDraft(d => ({ ...d, parts: [...d.parts, emptyPart(d.parts.length + 1)] }));
          setOpenPart(draft.parts.length);
        }}>
          <RiAddLine /> Add part
        </button>
      </div>

      {draft.parts.map((part, pi) => (
        <div key={pi} className="ks-card grm-form" style={{ marginTop: 12 }}>
          <div className="grm-part-head">
            <button className="grm-part-toggle" onClick={() => setOpenPart(openPart === pi ? -1 : pi)}>
              <strong>{draft.number}.{pi + 1}</strong>
              <span>{part.title || 'Untitled part'}</span>
              {openPart === pi ? <RiArrowUpSLine size={18} /> : <RiArrowDownSLine size={18} />}
            </button>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn-icon" title="Move up" disabled={pi === 0}
                      onClick={() => setDraft(d => ({ ...d, parts: move(d.parts, pi, pi - 1) }))}>
                <RiArrowUpSLine size={15} />
              </button>
              <button className="btn-icon" title="Move down" disabled={pi === draft.parts.length - 1}
                      onClick={() => setDraft(d => ({ ...d, parts: move(d.parts, pi, pi + 1) }))}>
                <RiArrowDownSLine size={15} />
              </button>
              <button className="btn-icon danger" title="Delete part"
                      onClick={() => setDraft(d => ({ ...d, parts: d.parts.filter((_, i) => i !== pi) }))}>
                <RiDeleteBinLine size={15} />
              </button>
            </div>
          </div>

          {openPart === pi && (
            <>
              <label>Part title
                <input className="ks-input" value={part.title}
                       onChange={e => setPart(pi, 'title', e.target.value)} placeholder="A は B です" />
              </label>
              <div className="ks-grid-2">
                <label>Pattern
                  <input className="ks-input jp" value={part.pattern}
                         onChange={e => setPart(pi, 'pattern', e.target.value)} placeholder="A は B です" />
                </label>
                <label>Pattern in English
                  <input className="ks-input" value={part.patternEn}
                         onChange={e => setPart(pi, 'patternEn', e.target.value)} placeholder="A is B" />
                </label>
              </div>
              <label>Explanation
                <textarea className="ks-textarea" rows={3} value={part.explanation}
                          onChange={e => setPart(pi, 'explanation', e.target.value)} />
              </label>
              <label>Notes — one per line
                <textarea className="ks-textarea" rows={3} value={(part.notes || []).join('\n')}
                          onChange={e => setPart(pi, 'notes', e.target.value.split('\n').filter(Boolean))} />
              </label>

              {/* ── Sentences ───────────────────────────────────── */}
              <div className="grm-subhead" style={{ marginTop: 18 }}>
                <h3 style={{ fontSize: 14, fontWeight: 900 }}>Sentences ({part.sentences.length})</h3>
                <button className="btn-ghost" onClick={() =>
                  setPart(pi, 'sentences', [...part.sentences, emptySentence()])}>
                  <RiAddLine /> Add sentence
                </button>
              </div>

              {part.sentences.map((s, si) => (
                <div key={si} className="grm-sentence">
                  <div className="grm-sentence-head">
                    <strong>Sentence {si + 1}</strong>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-icon" title="Fill Japanese / romaji / literal from the tokens"
                              onClick={() => deriveFromTokens(pi, si)}>
                        <RiFileCopyLine size={15} />
                      </button>
                      <button className="btn-icon danger" title="Delete sentence"
                              onClick={() => setPart(pi, 'sentences', part.sentences.filter((_, j) => j !== si))}>
                        <RiDeleteBinLine size={15} />
                      </button>
                    </div>
                  </div>

                  {/* Tokens first — they are the source the rest is built from. */}
                  <div className="grm-tokens">
                    <div className="grm-token-row head">
                      <span>Japanese</span><span>Romaji</span><span>Meaning</span>
                      <span>Role</span><span>Note shown to learners</span><span />
                    </div>
                    {s.tokens.map((tok, ti) => (
                      <div key={ti} className="grm-token-row">
                        <input className="ks-input" value={tok.jp} placeholder="は"
                               onChange={e => setToken(pi, si, ti, 'jp', e.target.value)} />
                        <input className="ks-input" value={tok.romaji} placeholder="wa"
                               onChange={e => setToken(pi, si, ti, 'romaji', e.target.value)} />
                        <input className="ks-input" value={tok.gloss} placeholder="(topic marker)"
                               onChange={e => setToken(pi, si, ti, 'gloss', e.target.value)} />
                        <select className="ks-select" value={tok.role}
                                onChange={e => setToken(pi, si, ti, 'role', e.target.value)}>
                          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <input className="ks-input" value={tok.note} placeholder="Why this word behaves the way it does"
                               onChange={e => setToken(pi, si, ti, 'note', e.target.value)} />
                        <button className="btn-icon danger" title="Remove word"
                                onClick={() => setSentence(pi, si, 'tokens', s.tokens.filter((_, k) => k !== ti))}>
                          <RiDeleteBinLine size={14} />
                        </button>
                      </div>
                    ))}
                    <button className="btn-ghost admin-token-add"
                            onClick={() => setSentence(pi, si, 'tokens', [...s.tokens, emptyToken()])}>
                      <RiAddLine size={14} /> Add word
                    </button>
                  </div>

                  <div className="ks-grid-2">
                    <label>Japanese sentence
                      <input className="ks-input jp" value={s.jp}
                             onChange={e => setSentence(pi, si, 'jp', e.target.value)} placeholder="これはペンです" />
                    </label>
                    <label>Romaji
                      <input className="ks-input" value={s.romaji}
                             onChange={e => setSentence(pi, si, 'romaji', e.target.value)} placeholder="Kore wa pen desu" />
                    </label>
                  </div>
                  <label>English — this is the answer for the translation drill
                    <input className="ks-input" value={s.en}
                           onChange={e => setSentence(pi, si, 'en', e.target.value)} placeholder="This is a pen" />
                  </label>

                  {/* English tiles, in English order, each paired with the
                      Japanese word it comes from. This is what puts kana
                      under an English tile in the translation drill. */}
                  <div className="grm-tokens">
                    <div className="grm-subhead" style={{ marginBottom: 2 }}>
                      <strong style={{ fontSize: 11, fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(247,243,234,0.45)' }}>
                        English tiles ({(s.enTokens || []).length})
                      </strong>
                      <button className="btn-ghost grm-token-add" onClick={() => deriveEnTokens(pi, si)}>
                        <RiFileCopyLine size={14} /> Split from English
                      </button>
                    </div>

                    {(s.enTokens || []).length > 0 && (
                      <div className="grm-token-row head grm-en-row">
                        <span>English word</span><span>Japanese</span><span>Romaji</span><span />
                      </div>
                    )}

                    {(s.enTokens || []).map((et, ti) => (
                      <div key={ti} className="grm-token-row grm-en-row">
                        <input className="ks-input" value={et.text} placeholder="Mr"
                               onChange={e => setEnToken(pi, si, ti, 'text', e.target.value)} />
                        <input className="ks-input" value={et.jp} placeholder="さん (blank for a/the)"
                               onChange={e => setEnToken(pi, si, ti, 'jp', e.target.value)} />
                        <input className="ks-input" value={et.romaji} placeholder="san"
                               onChange={e => setEnToken(pi, si, ti, 'romaji', e.target.value)} />
                        <button className="btn-icon danger" title="Remove tile"
                                onClick={() => setSentence(pi, si, 'enTokens', s.enTokens.filter((_, k) => k !== ti))}>
                          <RiDeleteBinLine size={14} />
                        </button>
                      </div>
                    ))}

                    <button className="btn-ghost grm-token-add"
                            onClick={() => setSentence(pi, si, 'enTokens', [...(s.enTokens || []), emptyEnToken()])}>
                      <RiAddLine size={14} /> Add tile
                    </button>

                    {(s.enTokens || []).length > 0
                      && (s.enTokens || []).map(t => t.text).join(' ') !== s.en && (
                      <div className="grm-error" style={{ marginTop: 8 }}>
                        These tiles spell &ldquo;{(s.enTokens || []).map(t => t.text).join(' ')}&rdquo;, which does not
                        match the English above. The drill will ignore them and fall back to plain word-splitting.
                      </div>
                    )}
                  </div>
                  <label>Word for word — in Japanese order
                    <input className="ks-input" value={s.literal}
                           onChange={e => setSentence(pi, si, 'literal', e.target.value)}
                           placeholder="this — (topic) — pen — is" />
                  </label>
                  <label>Note (optional)
                    <input className="ks-input" value={s.note}
                           onChange={e => setSentence(pi, si, 'note', e.target.value)} />
                  </label>

                  {s.tokens.filter(t => t.jp.trim()).length === 0 && (
                    <div className="grm-error" style={{ marginTop: 10 }}>
                      No words entered — this sentence will be skipped by every drill.
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

